import { FC, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { Dialog, DialogTitle, DialogContent, DialogActions, Grid } from '@mui/material';
import { useForm, FormProvider } from 'react-hook-form';
import styled from 'styled-components';
import { ajvResolver } from '@hookform/resolvers/ajv';
import { fullFormats } from 'ajv-formats/dist/formats';
import { addNotification } from '../../../../../store';
import {
  useCreateMicrositeTemplateBlockMutation,
  useUpdateMicrositeTemplateBlockMutation,
  Block,
  useCreateMicrositeTemplateBlockStylesheetMutation,
  useUpdateMicrositeTemplateBlockStylesheetMutation,
  useCreateMicrositeTemplateScriptMutation,
  useUpdateMicrositeTemplateScriptMutation,
} from '../../../../../services';
import { NgButton, DynamicFormField, FormInputWrapper } from '../../../../UI';
import { MonacoEditorTabs } from './MonacoEditorTabs';
import { BlockPreview } from './BlockPreview';
import { buttonVariants } from '@clatter/ui';

interface BlockCreateEditModalProps {
  blocks: Block[];
  block: Block | null;
  onClose: () => void;
}

const StyledDialogContent = styled(DialogContent)`
  width: 100%;
  min-height: 600px;
  padding: 24px !important;
`;

const StyledSection = styled.div`
  height: 100%;
  display: grid;
  grid-template-rows: max-content 1fr;

  &:has(.monaco-editor) {
    overflow: hidden;
  }
`;

const defaultVariables = {
  System: {
    BasePath: '/1/1',
    Pages: [{ Slug: 'preview', DisplayName: 'Preview' }],
    Page: {
      Slug: 'preview',
      DisplayName: 'Preview',
    },
    Sections: [],
    CurrentYear: new Date().getFullYear(),
  },
  Context: {
    MY_VARIABLE: 'MY_VALUE',
  },
};

const validationSchema = {
  type: 'object',
  properties: {
    name: {
      type: 'string',
      minLength: 1,
      pattern: '^\\S.*$',
      errorMessage: {
        minLength: 'Name is required',
        pattern: 'Name cannot start with whitespace',
      },
    },
    display_name: {
      type: 'string',
      minLength: 1,
      pattern: '^\\S.*$',
      errorMessage: {
        minLength: 'Display Name is required',
        pattern: 'Display Name cannot start with whitespace',
      },
    },
    type: { type: 'object' },
  },
  required: ['name', 'display_name', 'type'],
  errorMessage: {
    required: {
      name: 'Name is required',
      display_name: 'Display Name is required',
      type: 'Type is required',
    },
  },
  additionalProperties: true,
};

export const BlockCreateEditModal: FC<BlockCreateEditModalProps> = ({ blocks, block, onClose }) => {
  const dispatch = useDispatch();
  const isEditing = !!block;

  const formFields: any[] = useMemo(
    () => [
      {
        name: 'name',
        inputType: 'text',
        type: 'string',
        label: 'Name',
        required: true,
        order: 0,
      },
      {
        name: 'display_name',
        inputType: 'text',
        type: 'string',
        label: 'Display Name',
        required: true,
        order: 1,
      },
      {
        name: 'type',
        inputType: 'select',
        type: 'string',
        label: 'Type',
        required: true,
        order: 2,
        inputOptions: {
          selectOptions: [
            { label: 'Block', value: 'block' },
            { label: 'Layout', value: 'layout' },
          ],
        },
      },
    ],
    []
  );

  const formMethods = useForm({
    mode: 'onChange',
    resolver: ajvResolver(validationSchema as any, {
      formats: fullFormats,
      allErrors: true,
    }),
    defaultValues: isEditing && block ? {
      name: block.name || '',
      display_name: block.displayName || '',
      type: {
        label: block.type === 'layout' ? 'Layout' : 'Block',
        value: (block.type as 'layout' | 'block') || 'block',
      },
      html: block.html || '',
      css: block.blockStylesheet?.css || '',
      js: block.blockScript?.js || '',
      variables: JSON.stringify(block.variables || defaultVariables, null, 4),
    } : {
      name: '',
      display_name: '',
      type: { label: 'Block', value: 'block' },
      html: '',
      css: '',
      js: '',
      variables: JSON.stringify(defaultVariables, null, 4),
    },
  });

  const [createBlock, { isLoading: isCreating }] = useCreateMicrositeTemplateBlockMutation();
  const [updateBlock, { isLoading: isUpdating }] = useUpdateMicrositeTemplateBlockMutation();
  const [createBlockStylesheet, { isLoading: isCreatingStylesheet }] =
    useCreateMicrositeTemplateBlockStylesheetMutation();
  const [updateBlockStylesheet, { isLoading: isUpdatingStylesheet }] =
    useUpdateMicrositeTemplateBlockStylesheetMutation();
  const [createScript, { isLoading: isCreatingScript }] = useCreateMicrositeTemplateScriptMutation();
  const [updateScript, { isLoading: isUpdatingScript }] = useUpdateMicrositeTemplateScriptMutation();

  const isLoading =
    isCreating || isUpdating || isCreatingStylesheet || isUpdatingStylesheet || isCreatingScript || isUpdatingScript;

  const {
    formState: { isDirty, errors, isValid },
    watch,
    setValue,
  } = formMethods;
  const html = watch('html');
  const css = watch('css');
  const js = watch('js');
  const variables = watch('variables');

  const handleSubmit = formMethods.handleSubmit(async (data) => {
    try {
      const blockPayload = {
        name: data.name.trim(),
        display_name: data.display_name.trim(),
        type: data.type.value,
        html: data.html,
        variables: JSON.parse(data.variables),
      };

      let blockResult;
      if (isEditing && block) {
        blockResult = await updateBlock({
          blockId: block.id,
          data: blockPayload,
        }).unwrap();
      } else {
        blockResult = await createBlock({
          data: blockPayload,
        }).unwrap();
      }

      // Handle CSS (theme)
      const stylesheetPayload = {
        display_name: data.display_name,
        css: data.css || '',
      };

      if (block?.blockStylesheet) {
        // Update existing stylesheet (even if empty)
        await updateBlockStylesheet({
          stylesheetId: block.blockStylesheet.id,
          data: stylesheetPayload,
        }).unwrap();
      } else if (data.css?.trim()) {
        // Only create new stylesheet if there's content
        const stylesheet = await createBlockStylesheet({
          data: stylesheetPayload,
        }).unwrap();

        await updateBlock({
          blockId: blockResult.id,
          data: {
            block_stylesheet_id: stylesheet.id,
          },
        }).unwrap();
      }

      // Handle JS (script)
      const scriptPayload = {
        display_name: data.display_name,
        js: data.js || '',
      };

      if (block?.blockScript) {
        // Update existing script (even if empty)
        await updateScript({
          scriptId: block.blockScript.id,
          data: scriptPayload,
        }).unwrap();
      } else if (data.js?.trim()) {
        // Only create new script if there's content
        const script = await createScript({
          data: scriptPayload,
        }).unwrap();

        await updateBlock({
          blockId: blockResult.id,
          data: {
            block_script_id: script.id,
          },
        }).unwrap();
      }

      dispatch(
        addNotification({
          type: 'success',
          message: `Block ${isEditing ? 'updated' : 'created'} successfully`,
        }),
      );

      if (!isEditing) {
        onClose();
      }
    } catch (error) {
      dispatch(
        addNotification({
          type: 'error',
          message: `Failed to ${isEditing ? 'update' : 'create'} block`,
        }),
      );
    }
  });

  return (
    <Dialog open onClose={onClose} maxWidth={false} fullWidth>
      <DialogTitle>{isEditing ? 'Edit Block' : 'Create Block'}</DialogTitle>

      <StyledDialogContent>
        <FormProvider {...formMethods}>
          <Grid container spacing={3}>
            <Grid size={12}>
              <StyledSection>
                <Grid container spacing={2}>
                  {formFields.map((field) => {
                    const error = errors?.[field.name as keyof typeof errors];
                    return (
                      <Grid size={{ xs: 12, md: 4 }} key={field.name}>
                        <FormInputWrapper
                          label={field.label}
                          required={field.required}
                          errorMessage={(error?.message as string) || null}
                        >
                          <DynamicFormField data={field} disabled={isLoading} />
                        </FormInputWrapper>
                      </Grid>
                    );
                  })}
                </Grid>
              </StyledSection>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <StyledSection>
                <h3>Code Editor</h3>
                <MonacoEditorTabs
                  htmlCode={html}
                  cssCode={css}
                  jsCode={js}
                  variablesCode={variables}
                  onHtmlChange={(value) => setValue('html', value, { shouldDirty: true })}
                  onCssChange={(value) => setValue('css', value, { shouldDirty: true })}
                  onJsChange={(value) => setValue('js', value, { shouldDirty: true })}
                  onVariablesChange={(value) => setValue('variables', value, { shouldDirty: true })}
                  isLoading={isLoading}
                />
              </StyledSection>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <StyledSection>
                <h3>Preview</h3>
                <BlockPreview blocks={blocks} htmlCode={html} cssCode={css} jsCode={js} variables={variables} />
              </StyledSection>
            </Grid>
          </Grid>
        </FormProvider>
      </StyledDialogContent>

      <DialogActions>
        <NgButton onClick={onClose} variant={buttonVariants.clear} disabled={isLoading}>
          Cancel
        </NgButton>
        <NgButton onClick={() => handleSubmit()} disabled={isLoading || !isValid || (isEditing && !isDirty)}>
          {isLoading ? 'Saving...' : isEditing ? 'Update' : 'Create'}
        </NgButton>
      </DialogActions>
    </Dialog>
  );
};
